<template>
  <section
    v-if="workPartDetails"
    class="part-details-card-body"
  >
    <section class="part-details-card-body__main">
      <div class="part-details-card-body__main__article">
        <div class="part-details-card-body__main__article__name">
          {{ workPartDetails.NombreArticulo }}
        </div>

        <div class="part-details-card-body__main__article__serial-number">
          {{ workPartDetails.NumeroSerie }}
        </div>
      </div>

      <div class="part-details-card-body__main__actions">
        <PCPartsDetailsCardMainBodyMainActions />

        <div>
          <f7-button
            type="button"
            title="Más opciones"
            @click="isSecondaryActionsOpened = true"
          >
            <img src="@/static/icons/ic_options.svg">
          </f7-button>
        </div>
      </div>
    </section>

    <section class="part-details-card-body__observations">
      <div>
        <label for="part-observations">Observaciones del parte</label>
        <UCTextarea
          id="part-observations"
          v-model="partObservations"
          class="uc-textarea"
          :disabled="isSavingPartObservations"
        />

        <TransitionGroup name="observations">
          <div
            v-if="partObservations != null && partObservations !== workPartDetails.ObservacionesParte"
            class="part-details-card-body__observations__actions"
          >
            <f7-button
              :disabled="isSavingPartObservations"
              fill
              small
              color="red"
              :title="$t('common.cancel')"
              @click="partObservations = workPartDetails.ObservacionesParte"
            >
              <UCSvgIcon
                name="fa6-solid-xmark"
                color="#fff"
              />
            </f7-button>

            <f7-button
              :preloader="isSavingPartObservations"
              :loading="isSavingPartObservations"
              :disabled="isSavingPartObservations"
              fill
              small
              color="green"
              :title="$t('common.confirm')"
              @click="confirmPartObservations"
            >
              <UCSvgIcon name="fa6-solid-circle-check" />
            </f7-button>
          </div>
        </TransitionGroup>
      </div>

      <div>
        <label for="order-observations">Observaciones de la orden</label>
        <UCTextarea
          id="order-observations"
          v-model="orderObservations"
          class="uc-textarea"
          :disabled="isSavingOrderObservations"
        />

        <TransitionGroup name="observations">
          <div
            v-if="orderObservations != null && orderObservations !== workPartDetails.ObservacionesOrden"
            class="part-details-card-body__observations__actions"
          >
            <f7-button
              :disabled="isSavingOrderObservations"
              small
              fill
              color="red"
              :title="$t('common.cancel')"
              @click="orderObservations = workPartDetails.ObservacionesOrden"
            >
              <UCSvgIcon
                name="fa6-solid-xmark"
                color="#fff"
              />
            </f7-button>

            <f7-button
              :preloader="isSavingOrderObservations"
              :loading="isSavingOrderObservations"
              :disabled="isSavingOrderObservations"
              small
              fill
              color="green"
              :title="$t('common.confirm')"
              @click="confirmOrderObservations"
            >
              <UCSvgIcon name="fa6-solid-circle-check" />
            </f7-button>
          </div>
        </TransitionGroup>
      </div>
    </section>

    <PCPartsDetailsCardMainBodySecondaryActions v-model:opened="isSecondaryActionsOpened" />
  </section>
</template>

<script setup>
// @ts-check

import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairPartsStore } from '@/js/stores/repairParts';

import UCTextarea from '@/components/common/UCTextarea.vue';
import { callModifyOrderObservations, callModifyPartObservations } from '@/js/services/repairs/ApiRepairs';
import { ConfirmDialogGH, HiNotification } from '@/js/common/HINotificationSupport';
import { logError } from '@/js/utils/log';

import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCPartsDetailsCardMainBodyMainActions from './PCPartsDetailsCardMainBodyMainActions.vue';
import PCPartsDetailsCardMainBodySecondaryActions from './PCPartsDetailsCardMainBodySecondaryActions.vue';

/** @import { Ref } from 'vue' */

/** @type {Ref<boolean>} */
const isSecondaryActionsOpened = ref(false);

/** @type {Ref<string | null>} */
const partObservations = ref(null);
const isSavingPartObservations = ref(false);

/** @type {Ref<string | null>} */
const orderObservations = ref(null);
const isSavingOrderObservations = ref(false);

const { workPartDetails } = storeToRefs(useRepairPartsStore());

async function savePartObservations() {
  if (!workPartDetails.value) {
    return;
  }

  try {
    isSavingPartObservations.value = true;

    const { data } = await callModifyPartObservations(
      workPartDetails.value.Id_ParteProduccion,
      partObservations.value || '',
    );

    if (data.Valor === true) {
      workPartDetails.value.ObservacionesParte = partObservations.value || '';
      HiNotification.Toast('Se han guardado correctamente las observaciones del parte');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se han podido guardar las observaciones del parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se han podido guardar las observaciones del parte');
  } finally {
    isSavingPartObservations.value = false;
  }
}

async function confirmPartObservations() {
  if (!workPartDetails.value) {
    return;
  }

  if (partObservations.value === workPartDetails.value.ObservacionesParte) {
    HiNotification.Toast('No se puede guardar el mismo contenido');
    return;
  }

  const resultConfirm = await ConfirmDialogGH(
    `Esta acción modificará las observaciones del parte.<br>Nuevo valor:<br>
        <strong>${partObservations.value}</strong>`,
    '¿Quiere continuar?',
  );

  if (resultConfirm) {
    savePartObservations();
  }
}

async function saveOrderObservations() {
  if (!workPartDetails.value) {
    return;
  }

  try {
    isSavingOrderObservations.value = true;

    const { data } = await callModifyOrderObservations(
      workPartDetails.value.Id_ParteProduccion,
      orderObservations.value || '',
    );

    if (data.Valor === true) {
      workPartDetails.value.ObservacionesOrden = orderObservations.value || '';
      HiNotification.Toast('Se han guardado correctamente las observaciones de la orden');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se han podido guardar las observaciones de la orden');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se han podido guardar las observaciones de la orden');
  } finally {
    isSavingOrderObservations.value = false;
  }
}

async function confirmOrderObservations() {
  if (!workPartDetails.value) {
    return;
  }

  if (orderObservations.value === workPartDetails.value.ObservacionesOrden) {
    HiNotification.Toast('No se puede guardar el mismo contenido');
    return;
  }

  const resultConfirm = await ConfirmDialogGH(
    `Esta acción modificará las observaciones de la orden.<br>Nuevo valor:<br>
    <strong>${orderObservations.value}</strong>`,
    '¿Quiere continuar?',
  );

  if (resultConfirm) {
    saveOrderObservations();
  }
}

onMounted(() => {
  if (workPartDetails.value) {
    partObservations.value = workPartDetails.value.ObservacionesParte;
    orderObservations.value = workPartDetails.value.ObservacionesOrden;
  }
});
</script>

<style scoped lang="scss">
.part-details-card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__main {
    display: flex;
    flex-direction: column;
    gap: 16px;

    &__article {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      &__name {
        color: #172b4d;
        font-weight: bold;
      }

      &__serial-number {
        color: var(--his-oliva);
        background-color: var(--his-silver);
        border-radius: 4px;
        padding: 4px;
        font-size: smaller;
      }
    }

    &__actions {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__observations {
    display: flex;
    flex-direction: row;
    gap: 16px;

    > div {
      display: flex;
      flex-direction: column;
      flex: 0 0 35%;

      label {
        color: #000;
        font-size: smaller;
      }
    }

    &__actions {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
      transition: 1s;

      > a {
        flex: 0 0 15%;
      }
    }
  }
}

.observations-move,
.observations-enter-active,
.observations-leave-active {
  transition: all 0.5s ease;
}

.observations-enter-from,
.observations-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
